import { App, Button, Tag, Tooltip, type TableColumnsType } from 'antd';
import { Copy, Pause, Pencil, Play, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ProTable from '@/components/pro/ProTable';
import { AgentName } from '@/components/AgentName';
import { UserName } from '@/components/UserName';
import {
  AccountStatus,
  ChannelType,
  type ChannelAccount,
} from '@/proto/agentplatform/v1/channel_pb';
import { copyToClipboard, formatTimestamp } from '@/utils/utils';
import { BRIDGE_CONNECTION_URL } from './helpers';

interface ChannelAccountTableProps {
  data: ChannelAccount[];
  loading?: boolean;
  /** 当前 tab 的 URL slug，进入详情时带上 ?type=<slug> 以便返回落回原 tab */
  typeSlug?: string;
  /** 启用/停用切换（根据 item.disabled 分流 enable/disable） */
  onToggleDisable: (item: ChannelAccount) => void;
  onEdit: (item: ChannelAccount) => void;
  onDelete: (item: ChannelAccount) => void;
  toggling?: boolean;
}

function formatTs(ts: bigint | number | undefined) {
  if (!ts || ts === 0n) return '—';
  return formatTimestamp(ts);
}

/** 表格视图：列定义 + 行操作（启用/停用/编辑/删除）。业务逻辑经回调透传，本组件只负责视图。 */
export default function ChannelAccountTable({
  data,
  loading,
  typeSlug,
  onToggleDisable,
  onEdit,
  onDelete,
  toggling,
}: ChannelAccountTableProps) {
  const navigate = useNavigate();
  const { message } = App.useApp();

  // 进入详情时带上当前 tab 的 slug，返回时据此落回原 tab。
  const detailHref = (id: string) =>
    typeSlug ? `/channel-accounts/${id}?type=${typeSlug}` : `/channel-accounts/${id}`;

  async function copyUrl(e: React.MouseEvent, url: string) {
    e.stopPropagation();
    const ok = await copyToClipboard(url);
    if (ok) message.success('已复制');
    else message.error('复制失败');
  }

  const columns: TableColumnsType<ChannelAccount> = [
    {
      title: '名称 / ID',
      minWidth: 160,
      key: 'name',
      render: (_, row) => (
        <div>
          <div className="tb-name">{row.name || '—'}</div>
          <div className="tb-id pp-mono">{row.id}</div>
        </div>
      ),
    },
    {
      title: '绑定 Agent',
      minWidth: 160,
      key: 'agentId',
      render: (_, row) =>
        row.agentId ? (
          <AgentName agentId={row.agentId} />
        ) : (
          <span style={{ color: 'var(--text-color-placeholder)' }}>—</span>
        ),
    },
    {
      title: '连接地址',
      minWidth: 260,
      key: 'connectUrl',
      render: (_, row) => {
        if (row.type !== ChannelType.BRIDGE) {
          return <span style={{ color: 'var(--text-color-placeholder)' }}>—</span>;
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <code className="pp-mono" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {BRIDGE_CONNECTION_URL}
            </code>
            <Tooltip title="复制">
              <Button
                type="text"
                size="small"
                icon={<Copy size={12} />}
                onClick={(e) => copyUrl(e, BRIDGE_CONNECTION_URL)}
              />
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: '状态',
      key: 'status',
      minWidth: 90,
      render: (_, row) => (
        <Tag color={row.status === AccountStatus.ONLINE ? 'success' : 'default'}>
          {row.status === AccountStatus.ONLINE ? '已启用' : '已停用'}
        </Tag>
      ),
    },
    {
      title: '最后离线时间',
      key: 'lastOnline',
      minWidth: 160,
      render: (_, r) =>
        r?.firstOnlineAt ? (
          formatTimestamp(r?.lastOfflineAt)
        ) : (
          <span style={{ color: 'var(--text-color-placeholder)' }}>—</span>
        ),
    },
    {
      title: '创建者',
      key: 'creator',
      minWidth: 160,
      render: (_, row) => <UserName userId={row.userId} />,
    },
    {
      title: '创建时间',
      key: 'createdAt',
      minWidth: 160,
      render: (_, row) => formatTs(row.createdAt),
    },
    {
      title: '更新时间',
      key: 'updatedAt',
      minWidth: 160,
      render: (_, row) => formatTs(row.updatedAt),
    },
    {
      title: '操作',
      key: '__actions',
      minWidth: 130,
      align: 'left',
      fixed: 'right',
      render: (_, row) => (
        <>
          <Tooltip title={row.disabled ? '启用' : '停用'}>
            <Button
              type="text"
              size="small"
              disabled={toggling}
              icon={row.disabled ? <Play size={14} /> : <Pause size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                onToggleDisable(row);
              }}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button
              type="text"
              size="small"
              icon={<Pencil size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(row);
              }}
            />
          </Tooltip>
          <Tooltip title="删除">
            <Button
              type="text"
              size="small"
              icon={<Trash2 size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(row);
              }}
            />
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <ProTable<ChannelAccount>
      data={data}
      loading={loading}
      rowKey="id"
      columns={columns}
      scroll={{ x: 'max-content' }}
      onRow={(row) => ({
        onClick: () => navigate(detailHref(row.id)),
        style: { cursor: 'pointer' },
      })}
    />
  );
}
